import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Trash2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useSession } from "@/App";
import {
  AdminStatsCard,
  AssessmentRow,
  ElementLegendCard,
  DeleteConfirmationDialog
} from "@/components/AdminSections";
import { useAdminDashboard } from "@/hooks/useAdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HEADER_COLUMNS = ["Name", "Gender", "Age", "Experience", "S1", "S2", "S3", "S4", "S5", "Status", "Date"];

export default function AdminPage() {
  const navigate = useNavigate();
  const { session, clearSession } = useSession();
  const admin = useAdminDashboard({ apiBase: API, session, navigate });

  const handleLogout = () => {
    clearSession();
    navigate("/");
    toast.success("Logged out successfully");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600">View and manage Brain 360 assessment entries</p>
          </div>
          <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
            <LogOut className="w-4 h-4" />
            Exit Admin
          </Button>
        </div>

        <AdminStatsCard
          assessments={admin.assessments}
          selectedCount={admin.selectedIds.size}
          loading={admin.loading}
          onRefresh={admin.fetchAssessments}
        />

        <div className="flex gap-2 flex-wrap">
          <Button onClick={admin.handleExportCSV} className="bg-green-600 hover:bg-green-700">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          {admin.someSelected && (
            <Button onClick={() => admin.handleDeleteClick('selected')} variant="destructive">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected ({admin.selectedIds.size})
            </Button>
          )}
          <Button
            onClick={() => admin.handleDeleteClick('all')}
            variant="outline"
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete All
          </Button>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox checked={admin.allSelected} onCheckedChange={admin.handleSelectAll} aria-label="Select all" />
                    </TableHead>
                    {HEADER_COLUMNS.map((col, idx) => (
                      <TableHead key={col} className={idx >= 4 && idx <= 8 ? "text-center" : ""}>{col}</TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admin.loading && (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-gray-500">Loading...</TableCell>
                    </TableRow>
                  )}
                  {!admin.loading && admin.assessments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13} className="text-center py-8 text-gray-500">No assessment entries found</TableCell>
                    </TableRow>
                  )}
                  {!admin.loading && admin.assessments.map((assessment) => (
                    <AssessmentRow
                      key={assessment.id}
                      assessment={assessment}
                      selected={admin.selectedIds.has(assessment.id)}
                      onSelect={admin.handleSelectOne}
                      onViewReport={admin.handleViewReport}
                      onDelete={admin.handleDeleteClick}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <ElementLegendCard />
      </div>

      <DeleteConfirmationDialog
        open={admin.showDeleteDialog}
        onOpenChange={admin.setShowDeleteDialog}
        deleteTarget={admin.deleteTarget}
        totalAssessments={admin.assessments.length}
        selectedCount={admin.selectedIds.size}
        onConfirm={admin.handleDelete}
      />
    </div>
  );
}
